import type {
  ProductFile,
  ProductVariant,
} from '@eliadmin/core/src/api/products/types/product-types';
import type { ProductDetailsWithSlug } from '@eliadmin/core/src/stores/variant-store';

import { imageSource } from '@eliadmin/core/src/utils/image-alt-utils';

/** The API may send `null` – or, for broken data, no array at all – instead of an empty list. */
const asList = <T>(items: T[] | null | undefined): T[] => (Array.isArray(items) ? items : []);

/**
 * Images of the gallery, same selection as the core gallery: the images of the selected variant when
 * „gallery with image variants“ is on and the variant has some, otherwise the product image, the
 * product images and the images of all variants. Entries without a source and repeated media (the
 * API may list the main image again in `images`) are dropped.
 */
export function collectGalleryImages(
  variantImagesOnly: boolean,
  product: ProductDetailsWithSlug,
  selectedVariant?: ProductVariant | null,
): ProductFile[] {
  const variantImages = asList(selectedVariant?.images);
  const images: (ProductFile | null | undefined)[] =
    variantImagesOnly && variantImages.some(image => imageSource(image))
      ? variantImages
      : [
          product.image,
          ...asList(product.images),
          ...asList(product.variants).flatMap(variant => asList(variant?.images)),
        ];

  const seen = new Set<number>();
  return images.filter((image): image is ProductFile => {
    if (!image || !imageSource(image)) return false;
    if (typeof image.id !== 'number') return true;
    if (seen.has(image.id)) return false;
    seen.add(image.id);
    return true;
  });
}
